import { Switch, Route, Redirect } from 'wouter';
import { useRef, useEffect } from 'react';
import { queryClient, setAccessTokenGetter } from './lib/queryClient';
import { checkAndInvalidate } from '@/lib/digestClient';
import { QueryClientProvider } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { loadRuntimeConfig } from '@/lib/runtimeConfig';
import { initAnalytics, trackLogin } from '@/lib/analytics';
import RouteTracker from '@/components/RouteTracker';
import { ThemeProvider } from '@/lib/ThemeProvider';
import { ConfigProvider } from '@/components/ConfigProvider';
import NotFound from '@/pages/not-found';
import Landing from '@/pages/Landing';
import Privacy from '@/pages/Privacy';
import Terms from '@/pages/Terms';
import Home from '@/pages/Home';
import RecipesPage from '@/pages/RecipesPage';
import CollectionsPage from '@/pages/CollectionsPage';
import RecipeView from '@/pages/RecipeView';
import RecipeEdit from '@/pages/RecipeEdit';
import RecipeGroupDetail from '@/pages/RecipeGroupDetail';
import Help from '@/pages/Help';
import AIChefChatPage from '@/pages/AIChefChatPage';
import SharedRecipeView from '@/pages/SharedRecipeView';
import Filters from '@/pages/Filters';
import Household from '@/pages/Household';
import { Toaster } from '@/components/ui/toaster';
import { ChatProvider } from '@/contexts/ChatContext';
import DesktopLayout from '@/components/DesktopLayout';
import ImportSheet from '@/components/ImportSheet';
import { ImportSheetProvider } from '@/contexts/ImportSheetContext';
import { ShareTargetProvider } from '@/contexts/ShareTargetContext';
import { useShareTargetListener } from '@/hooks/useShareTargetListener';
import ShareTargetPrompt from '@/components/ShareTargetPrompt';

// Public routes component (no auth required)
function PublicRoutes() {
  return (
    <QueryClientProvider client={queryClient}>
      <Switch>
        <Route path="/" component={Landing} />
        <Route path="/privacy" component={Privacy} />
        <Route path="/terms" component={Terms} />
        <Route path="/recipes/shared/:token" component={SharedRecipeView} />
        {/* Redirect any unknown path to landing — avoids brief 404 when
            Auth0 clears the session before the appUrlOpen navigate('/') fires. */}
        <Route component={() => <Redirect to="/" />} />
      </Switch>
      <Toaster />
    </QueryClientProvider>
  );
}

// Private (authenticated) routes
function PrivateRoutes() {
  return (
    <Switch>
      <Route path="/" component={Home} />
      <Route path="/filters" component={Filters} />
      <Route path="/ai-chef" component={AIChefChatPage} />
      <Route path="/recipes" component={RecipesPage} />
      <Route path="/recipes/new" component={RecipeEdit} />
      <Route path="/recipes/shared/:token" component={SharedRecipeView} />
      <Route path="/recipes/:id" component={RecipeView} />
      <Route path="/recipes/:id/edit" component={RecipeEdit} />
      <Route path="/recipe-groups" component={CollectionsPage} />
      <Route path="/recipe-groups/:id" component={RecipeGroupDetail} />
      <Route path="/help" component={Help} />
      <Route path="/household" component={Household} />
      <Route component={NotFound} />
    </Switch>
  );
}

function Router() {
  const { isAuthenticated, isLoading, getAccessToken } = useAuth();
  const loginTracked = useRef(false);

  // Must run regardless of auth state: stores any incoming share URL and
  // triggers login if unauthenticated. The WebView stays alive through the
  // native Browser.open login flow, so the pending URL survives in context.
  useShareTargetListener();

  useEffect(() => {
    if (isAuthenticated && !loginTracked.current) {
      loginTracked.current = true;
      trackLogin();
      // Check digest on startup so cached data is validated as soon as the
      // user is authenticated (catches staleness from being offline / closing the app).
      void checkAndInvalidate();
    }
  }, [isAuthenticated]);

  if (isLoading) {
    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}
      >
        <div>Loading...</div>
      </div>
    );
  }

  // If not authenticated yet, render ONLY public routes (no private providers that start queries)
  if (!isAuthenticated) {
    return <PublicRoutes />;
  }

  // Register token getter synchronously before PrivateRoutes renders,
  // ensuring the getter is wired before any child query fires.
  setAccessTokenGetter(async () => {
    if (isLoading || !isAuthenticated) return null;
    return await getAccessToken();
  });

  // Authenticated: wrap private routes in providers
  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider>
        <ChatProvider>
          <ImportSheetProvider>
            <DesktopLayout>
              <PrivateRoutes />
            </DesktopLayout>
            <ImportSheet />
            <ShareTargetPrompt />
            <Toaster />
          </ImportSheetProvider>
        </ChatProvider>
      </ConfigProvider>
    </QueryClientProvider>
  );
}

function App() {
  useEffect(() => {
    loadRuntimeConfig()
      .then(config => {
        if (config.ga4MeasurementId && config.features?.analytics) {
          initAnalytics(config.ga4MeasurementId);
        }
      })
      .catch(() => {});
  }, []);

  return (
    <ThemeProvider>
      <RouteTracker />
      <ShareTargetProvider>
        <Router />
      </ShareTargetProvider>
    </ThemeProvider>
  );
}

export default App;
